import { WebcastEntry } from "@kaltura/mediaspace-shared-types";
import { useContext, useEffect } from "react";
import { useButtonAnalytics, useCurrentTimeForAnimation } from "@kaltura/mediaspace-shared-hooks";
import { getEntryUrl } from "@kaltura/mediaspace-shared-utils";
import { NotificationContent } from "./NotificationContent";
import { Notification, NotificationType } from "./types";
import { useSidebarLayoutContext } from "@kaltura/ds-react-layouts";
import { SidebarButton } from "./sidebar/SidebarButton";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { UnisphereNotificationsContext } from "@unisphere/notifications-runtime-react";
import { NotificationRemoveReasonTypes, NotificationTypes } from "@unisphere/notifications-core";

const UPCOMING_TIME_WINDOW_SEC = 5;

export interface NotificationsManagerProps {
    channelId: number;
    sessions: WebcastEntry[];
    currentlyAttendingSessionId?: string;
    notificationTimeoutMS: number;
}

export const NotificationsManager = ({
    sessions = [],
    currentlyAttendingSessionId,
    channelId,
    notificationTimeoutMS = 6000,
}: NotificationsManagerProps) => {
    const now = useCurrentTimeForAnimation(undefined, 1000) / 1000;
    const { setIsOpened, setActiveButtonId } = useSidebarLayoutContext();
    const sendButtonAnalytics = useButtonAnalytics();
    const notifications = useContext(UnisphereNotificationsContext);

    // show notification using Unisphere notifications widget
    const showNotification = (notification: Notification) => {
        const unisphereNotification = notifications.addNotification({
            type: NotificationTypes.CustomToast,
            getElement: () => <NotificationContent notification={notification} uniqueId={unisphereNotification.id} />,
            options: {
                duration: notificationTimeoutMS,
            },
            callbacks: {
                onClose: (_id, reason) => {
                    if (reason === NotificationRemoveReasonTypes.CloseButton) {
                        sendButtonAnalytics(
                            "TV guide - Close notification",
                            ButtonClickAnalyticsType.CLOSE,
                            notification.analyticsEntryId
                        );
                    }
                },
            },
        });
    };

    // function to remove notification from cnc notifications container
    const closeNotification = (notificationId: string) => notifications.removeNotification(notificationId);

    const navigateToSession = (session: WebcastEntry, notificationId: string) => {
        closeNotification(notificationId);
        document.location.href = getEntryUrl(session.id, channelId, session.name);
    };

    const browseSessions = (notificationId: string) => {
        const isOnMediaPage = currentlyAttendingSessionId ?? false;
        //  open agenda section in sidebar layout only when in media page
        //  in channel page, user already sees the sessions
        if (isOnMediaPage) {
            // open agenda section in sidebar layout
            setActiveButtonId(SidebarButton.id);
            setIsOpened(true);
        }

        closeNotification(notificationId);
    };

    const prepareNotification = (sessionsToNotifyAbout: WebcastEntry[], gap?: number): Notification => {
        gap = gap ?? -1;
        const isWithGap = gap > 0;
        // show gap notification only if has a gap and only single session to notify about
        if (isWithGap && sessionsToNotifyAbout.length === 1) {
            return {
                type: NotificationType.gap,
                gapInMinutes: gap,
                session: sessionsToNotifyAbout[0],
                analyticsEntryId: currentlyAttendingSessionId,
                onGoToSession: (session: WebcastEntry, notificationId: string) =>
                    navigateToSession(session, notificationId),
                onBrowseSession: browseSessions,
            };
        }

        return sessionsToNotifyAbout.length > 1
            ? {
                  type: NotificationType.many,
                  upcomingSessions: sessionsToNotifyAbout,
                  analyticsEntryId: currentlyAttendingSessionId,
                  onBrowseSessions: browseSessions,
              }
            : {
                  type: NotificationType.single,
                  timeTillStart: UPCOMING_TIME_WINDOW_SEC,
                  session: sessionsToNotifyAbout[0],
                  analyticsEntryId: currentlyAttendingSessionId,
                  onWatch: (session: WebcastEntry, notificationId: string) =>
                      navigateToSession(session, notificationId),
                  onStay: (notificationId: string) => closeNotification(notificationId),
              };
    };

    const isTimeArrived = (timestamp: number, timeDiff = 0): boolean => Math.floor(timestamp - now) === timeDiff;

    const notifyAboutNextSession = (sessionsAboutToStart: WebcastEntry[], sessionsStartingGap?: number) => {
        const notification = prepareNotification(sessionsAboutToStart, sessionsStartingGap);
        // don't show notification to user
        // if user already in media page for session which is about to start
        if (
            currentlyAttendingSessionId
            && notification.type === NotificationType.single
            && sessionsAboutToStart[0].id === currentlyAttendingSessionId
        ) {
            return;
        }
        showNotification(notification);
    };

    const handleCurrentlyAttendingSession = (
        currentlyAttendingSession: WebcastEntry,
        sessionsAboutToStart: WebcastEntry[]
    ) => {
        const nextSessionStartTime = sessionsAboutToStart[0].schedulingData.start.timestamp;
        const sessionsStartingGap = Math.round((nextSessionStartTime - now) / 60);
        if (isTimeArrived(currentlyAttendingSession.schedulingData.end.timestamp)) {
            // current session just ended
            notifyAboutNextSession(sessionsAboutToStart, sessionsStartingGap);
        }
        else if (!sessionsStartingGap && isTimeArrived(nextSessionStartTime)) {
            // current session not ended yet and another is starting now (if already ended currentlyAttendingSession will be null)
            notifyAboutNextSession(sessionsAboutToStart);
        }
    };

    const handleBrowsingEndedOrFutureSessions = (sessionsAboutToStart: any[]) => {
        const nextSessionStartTime = sessionsAboutToStart[0].schedulingData.start.timestamp;
        const shouldNotifyForSingleSession =
            sessionsAboutToStart.length === 1 && isTimeArrived(nextSessionStartTime, UPCOMING_TIME_WINDOW_SEC);
        const shouldNotifyForMultipleSessions = sessionsAboutToStart.length > 1 && isTimeArrived(nextSessionStartTime);
        if (shouldNotifyForSingleSession || shouldNotifyForMultipleSessions) {
            notifyAboutNextSession(sessionsAboutToStart);
        }
    };

    console.debug("Notification manager attached");

    // Effect to schedule notifications based on the next sessions
    useEffect(() => {
        if (!sessions?.length) {
            console.debug("no entries");
            return;
        }

        const upcomingSessions = sessions.filter((entry) => entry.schedulingData.start.timestamp > now);
        if (!upcomingSessions.length) {
            console.debug("no upcoming entries");
            return;
        }

        const currentLiveEntries = sessions.filter(
            (entry) => entry.schedulingData.start.timestamp <= now && entry.schedulingData.end.timestamp >= now
        );
        const currentlyAttendingSession =
            currentLiveEntries.find((entry) => entry.id === currentlyAttendingSessionId) ?? null;
        const nextSessionStartTime = upcomingSessions[0].schedulingData.start.timestamp;
        const sessionsAboutToStart = upcomingSessions.filter(
            (entry) => entry.schedulingData.start.timestamp === nextSessionStartTime
        );

        // user is currently attending session (user on media page)
        if (currentlyAttendingSession) {
            handleCurrentlyAttendingSession(currentlyAttendingSession, sessionsAboutToStart);
        }
        else {
            // user browsing future sessions, ended sessions or user on channel page (aka sees sessions schedule)
            handleBrowsingEndedOrFutureSessions(sessionsAboutToStart);
        }
    }, [sessions, now, currentlyAttendingSessionId]);

    return null; // don't  render anything
};
